import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../../components/Header';
import { LoginModal } from '../../components/LoginModal';
import { ConversationPanel } from './components/ConversationPanel';
import { ScenePanel } from './components/ScenePanel';
import { useOpenSCAD } from '../../openscad/useOpenSCAD';
import { useAuth } from '../../contexts/AuthContext';
import { useConversation, createConversation, useConversationsQuery } from '../../services/conversationService';
import { useMessagesQuery, useInsertMessageMutation, useParametricChatMutation, useIsLoading } from '../../services/messageService';
import { uploadBase64Images } from '../../services/imageService';
import { getLocalHistory, addToLocalHistory, LocalConversation } from '../../services/localHistoryService';
import { Message, Conversation, Content } from '@shared/types';
import { ParameterWithPosition } from '../../components/ParameterHotspots';
import { parseOpenSCADParameters, updateParameterInCode } from '../../utils/parseOpenSCADParameters';
import '../../components/UserMenu.css';
import '../../components/Header.css';
import '../../components/LoginModal.css';
import './styles/viewer.css';

// Custom hook for mobile detection
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= breakpoint);
    
    // Check on mount
    checkMobile();
    
    // Listen for resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

export const Viewer = () => {
  const { user, loading: authLoading, signOut, signInAnonymously } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('');
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isCanvasExpanded, setIsCanvasExpanded] = useState(false);
  const [currentCode, setCurrentCode] = useState<string | null>(null);
  const [currentParameters, setCurrentParameters] = useState<ParameterWithPosition[]>([]);
  const showHotspots = true; // Hotspots are always enabled
  const baseCodeRef = useRef<string | null>(null); // Original code before parameter edits
  
  // Mobile detection
  const isMobile = useIsMobile(768);
  
  // Sidebar collapsed state - auto-collapse on mobile
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Auto-collapse sidebar on mobile when component mounts or screen size changes
  useEffect(() => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  }, [isMobile]);
  
  // Local history for unauthenticated users
  const [localHistory, setLocalHistory] = useState<LocalConversation[]>([]);
  
  // Get conversation ID from URL
  const conversationId = searchParams.get('c') || null;
  
  // Supabase queries
  const { conversation } = useConversation(conversationId);
  const { data: messages = [] } = useMessagesQuery(conversationId);
  const { data: conversationsList = [] } = useConversationsQuery();
  const isAILoading = useIsLoading(conversationId);
  
  // Load local history on mount
  useEffect(() => {
    setLocalHistory(getLocalHistory());
  }, []);
    
  // Message mutations - using individual hooks for flexibility with new conversations
  const { mutateAsync: insertMessage } = useInsertMessageMutation();
  const { mutateAsync: sendToChat, isPending: isSending } = useParametricChatMutation({
    conversationId: conversationId || '',
  });
  
  // OpenSCAD WASM hook
  const { compileScad, exportStl, isCompiling, output, isError, error } = useOpenSCAD();
  
  // Track the last code we compiled to avoid re-compiling
  const lastCompiledCodeRef = useRef<string | null>(null);
  
  // When OpenSCAD output changes, load it into the viewer
  useEffect(() => {
    console.log('[Viewer] OpenSCAD output changed:', { hasOutput: !!output, hasLoadSTLBlob: !!window.loadSTLBlob });
    if (output && window.loadSTLBlob) {
      console.log('[Viewer] Loading STL blob into viewer, size:', output.size);
      window.loadSTLBlob(output);
      setStatus(''); // Clear status after successful render
      setShowDownloadButton(true);
    }
  }, [output]);

  // When OpenSCAD has an error - log to console only
  useEffect(() => {
    if (isError && error) {
      console.error('[Viewer] OpenSCAD error:', error.message, error);
    }
  }, [isError, error]);
      
  // When compiling - log to console only
  useEffect(() => {
    console.log('[Viewer] isCompiling changed:', isCompiling);
    if (isCompiling) {
      console.log('[Viewer] Compiling OpenSCAD code...');
        }
  }, [isCompiling]);
  
  // Compile OpenSCAD code from the latest message artifact
  useEffect(() => {
    console.log('[Viewer] Messages changed, count:', messages.length);
    
    if (messages.length > 0) {
      // Find the last message with an artifact
      const lastArtifactMessage = [...messages]
        .reverse()
        .find((m: Message) => m.role === 'assistant' && m.content?.artifact?.code);
        
      // Debug: Log all assistant messages to see what we're getting
      const assistantMessages = messages.filter((m: Message) => m.role === 'assistant');
      console.log('[Viewer] Assistant messages:', assistantMessages.length);
      if (assistantMessages.length > 0) {
        const lastAssistant = assistantMessages[assistantMessages.length - 1];
        console.log('[Viewer] Last assistant message content:', {
          hasContent: !!lastAssistant.content,
          hasArtifact: !!lastAssistant.content?.artifact,
          hasCode: !!lastAssistant.content?.artifact?.code,
          codeLength: lastAssistant.content?.artifact?.code?.length,
          text: lastAssistant.content?.text?.substring(0, 100),
        });
      }
        
      if (lastArtifactMessage?.content?.artifact?.code) {
        const code = lastArtifactMessage.content.artifact.code;
        console.log('[Viewer] Found artifact code, length:', code.length);
        
        if (code !== lastCompiledCodeRef.current) {
          console.log('[Viewer] Code is new, compiling...');
          lastCompiledCodeRef.current = code;
          baseCodeRef.current = code; // Store base code for parameter updates
          setCurrentCode(code);
          compileScad(code);
          
          // Parse parameters directly from the OpenSCAD code
          const parsedParams = parseOpenSCADParameters(code);
          console.log('[Viewer] Parsed parameters:', parsedParams.length);
          
          if (parsedParams.length > 0) {
            const params: ParameterWithPosition[] = parsedParams.map(p => ({
              ...p,
              type: p.type as ParameterWithPosition['type'],
            }));
            setCurrentParameters(params);
          } else {
            setCurrentParameters([]);
          }
        } else {
          console.log('[Viewer] Code unchanged, skipping compile');
        }
      } else {
        console.log('[Viewer] No artifact code found in messages');
    }
    }
  }, [messages, compileScad]);

  // Update code with new parameter values (for live preview)
  const updateCodeWithParameter = useCallback((name: string, value: number | string | boolean) => {
    if (!baseCodeRef.current) return;
    
    // Update the parameter in currentParameters state
    setCurrentParameters(prev => 
      prev.map(p => p.name === name ? { ...p, value } : p)
    );
    
    // Update the code with the new parameter value using utility
    const updatedCode = updateParameterInCode(baseCodeRef.current, name, value);
    
    if (updatedCode !== currentCode) {
      setCurrentCode(updatedCode);
    }
  }, [currentCode]);

  // Handle parameter change (live preview - just update display)
  const handleParameterChange = useCallback((name: string, value: number | string | boolean) => {
    updateCodeWithParameter(name, value);
  }, [updateCodeWithParameter]);

  // Handle parameter apply (actually recompile with new values)
  const handleParameterApply = useCallback((name: string, value: number | string | boolean) => {
    if (!baseCodeRef.current) return;
    
    // Update the base code with the new parameter value using utility
    baseCodeRef.current = updateParameterInCode(baseCodeRef.current, name, value);
    
    // Update current code and recompile
    setCurrentCode(baseCodeRef.current);
    lastCompiledCodeRef.current = baseCodeRef.current;
    compileScad(baseCodeRef.current);
    
    console.log('Updating model...');
  }, [compileScad]);
    
  // Note: Conversations are created when user sends first message (not on load)
  // This allows anonymous users to browse without creating empty conversations

  // Handle pending prompt from homepage
  const pendingPromptHandled = useRef(false);
  useEffect(() => {
    // Only run once after auth loading completes
    if (authLoading || pendingPromptHandled.current) return;
    
    const pendingPrompt = sessionStorage.getItem('pending_prompt');
    const pendingImages = sessionStorage.getItem('pending_images');
    const pendingModel = sessionStorage.getItem('pending_model');
    
    if (pendingPrompt || pendingImages) {
      pendingPromptHandled.current = true;
      sessionStorage.removeItem('pending_prompt');
      sessionStorage.removeItem('pending_images');
      sessionStorage.removeItem('pending_model');
      
      const images = pendingImages ? JSON.parse(pendingImages) : undefined;
      handleSendMessage(pendingPrompt || '', images, undefined, undefined, undefined, pendingModel || undefined);
    }
  }, [authLoading]);

  // Default model for Gemini (Pro Preview)
  const DEFAULT_MODEL = 'google/gemini-3-pro-preview';

  const handleSendMessage = async (
    message: string, 
    images?: string[],
    _clarificationAnswers?: Record<string, any>,
    _objectType?: string,
    _useDefaults?: boolean,
    model?: string
  ) => {
    if (!message.trim() && (!images || images.length === 0)) return;
    
    // Use provided model or default
    const selectedModel = model || DEFAULT_MODEL;
    
    // Auto sign-in anonymously if no user (silently, without prompting)
    if (!user) {
      try {
        await signInAnonymously();
        // Wait a moment for auth state to update
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Anonymous sign-in failed:', error);
        // Don't block the user - just show a message and continue
        // They can still interact with the UI, models just won't be saved
      }
    }
    
    // Create conversation if needed
    let convId = conversationId;
    if (!convId) {
      try {
        const newConv = await createConversation('New Design');
        convId = newConv.id;
        setSearchParams({ c: newConv.id });
      } catch (error) {
        console.error('Error creating conversation:', error);
        console.error('Error creating conversation');
        return;
      }
    }
    
    console.log('Creating model...');
    
    // Add to local history for unauthenticated users
    if (message.trim()) {
      addToLocalHistory(message.trim());
      setLocalHistory(getLocalHistory());
    }
    
    // Clear scene
    if (window.clearScene) {
      window.clearScene();
    }
    setShowDownloadButton(false);
    
    try {
      // Get current user for image upload
      const { data: { user: currentUser } } = await (await import('../../lib/supabase')).supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('Not authenticated');
      }
      
      // Build content
      const content: Content = {
        text: message,
        model: selectedModel,
      };
      
      // Upload images to Supabase storage
      if (images && images.length > 0) {
        try {
          const uploadedImages = await uploadBase64Images(images, currentUser.id, convId);
          content.images = uploadedImages.map(img => img.id);
          // Store the base64 images for display in conversation
          content.imageData = images;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          return;
        }
      }
      
      // Get parent message ID from current conversation
      const parentMessageId = conversation?.current_message_leaf_id ?? null;
      
      // Insert user message
      const userMessage = await insertMessage({
        role: 'user',
        content,
        parent_message_id: parentMessageId,
        conversation_id: convId,
      });
      
      // Send to AI chat
      await sendToChat({
        model: selectedModel,
        messageId: userMessage.id,
        conversationId: convId,
        thinking: content.thinking,
      });
      
      console.log('Model generated');
    } catch (error: any) {
      console.error('Error sending message:', error);
    }
  };

  const handleNewChat = async () => {
    // Auto sign-in anonymously if no user (silently)
    if (!user) {
      try {
        await signInAnonymously();
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Anonymous sign-in failed:', error);
        // Don't block - just continue without saving to database
      }
    }
    
    try {
      const newConv = await createConversation('New Design');
      setSearchParams({ c: newConv.id });
      
      // Clear scene
      if (window.clearScene) {
        window.clearScene();
        }
      setShowDownloadButton(false);
      setCurrentCode(null);
      lastCompiledCodeRef.current = null;
      setStatus('');
    } catch (error) {
      console.error('Error creating new chat:', error);
      }
  };

  const handleSwitchConversation = (conv: Conversation) => {
    setSearchParams({ c: conv.id });
    lastCompiledCodeRef.current = null; // Force recompile for new conversation
  };

  const handleDownload = async (mode: 'merged_stl' | 'assembly_step' | 'individual_parts') => {
    // For OpenSCAD models, export directly from browser
    if (currentCode && mode === 'merged_stl') {
      console.log('Exporting STL...');
      try {
        const stlBlob = await exportStl(currentCode);
        if (stlBlob) {
          const url = window.URL.createObjectURL(stlBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${conversation?.title || 'model'}.stl`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          console.log('STL downloaded successfully');
        } else {
          throw new Error('Export failed - no output');
        }
      } catch (error: any) {
        console.error('OpenSCAD export error:', error);
      }
      return;
    }
    
    console.log('Export not available for this format:', mode);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const formatTime = (date: Date | string | null | undefined) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Helper to detect if text is OpenSCAD code
  const isOpenSCADCode = (text: string): boolean => {
    if (!text || text.length < 50) return false;
    const codePatterns = [
      /\b(cube|sphere|cylinder|difference|union|translate|rotate)\s*\(/,
      /\$fn\s*=/,
      /^\s*\/\/\s*(Parameters|Resolution)/m,
      /^\s*\w+\s*=\s*\d+\s*;/m,
    ];
    const matches = codePatterns.filter(p => p.test(text)).length;
    return matches >= 2;
  };

  // Convert messages to conversation panel format
  // Filter out OpenSCAD code from AI messages - only show conversational text
  const conversationMessages = messages.map((m: Message) => {
    let displayContent = '';
    let codePreview = '';
    
    if (m.role === 'user') {
      displayContent = m.content?.text || '';
    } else {
      let text = m.content?.text || '';
      
      // Remove markdown code blocks
      text = text.replace(/```(?:openscad)?\s*[\s\S]*?```/g, '').trim();
      
      // Check if the remaining text is still OpenSCAD code (raw code)
      if (isOpenSCADCode(text)) {
        // Store code preview for collapsible display
        codePreview = text.substring(0, 200) + (text.length > 200 ? '...' : '');
        displayContent = 'Model generated ✓';
      } else if (m.content?.artifact?.code) {
        // Has artifact code but text might be descriptive
        displayContent = text || 'Model generated ✓';
      } else {
        displayContent = text;
      }
    }
    
    return {
    id: m.id,
    sender: m.role === 'user' ? 'user' as const : 'ai' as const,
      content: displayContent,
    timestamp: new Date(m.created_at),
      images: m.content?.imageData || m.content?.images || undefined,
      codePreview: codePreview || undefined,
    };
  });

  // Convert conversations list to format expected by ConversationPanel
  // Combine Supabase conversations with local history
  const supabaseConversations = conversationsList.map((c: Conversation) => ({
    id: c.id as unknown as number, // Legacy compat
    title: c.title,
    messages: [],
    timestamp: new Date(c.updated_at || c.created_at || Date.now()),
    isLocal: false,
  }));
  
  const localConversations = localHistory.map((c: LocalConversation) => ({
    id: c.id as unknown as number,
    title: c.title,
    messages: [{ sender: 'user' as const, content: c.prompt }],
    timestamp: c.timestamp,
    isLocal: true,
  }));
  
  // Merge and sort by timestamp (most recent first)
  const conversationsForPanel = [...supabaseConversations, ...localConversations]
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    .slice(0, 50); // Limit to 50 items

  if (authLoading) {
    return (
      <div className="viewer-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-page">
      <Header
        user={user as any} // Firebase User type compatibility
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignOut={handleSignOut}
        showHistory={true}
        onHistoryToggle={() => {}}
        historyCount={conversationsList.length}
        conversations={conversationsForPanel}
        onSelectConversation={(id) => {
          const conv = conversationsList.find((c: Conversation) => c.id === String(id));
          if (conv) handleSwitchConversation(conv);
        }}
        formatTime={formatTime}
      />
      
      <div className={`main-container ${isCanvasExpanded ? 'canvas-expanded' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Sidebar toggle button - visible when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button 
            className="sidebar-expand-btn"
            onClick={() => setIsSidebarCollapsed(false)}
            title="Show chat panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
        )}
        
        <ConversationPanel
          conversations={conversationsForPanel}
          currentConversationId={conversationId as unknown as number}
          onNewChat={handleNewChat}
          onSwitchConversation={(id) => {
            const conv = conversationsList.find((c: Conversation) => c.id === String(id));
            if (conv) handleSwitchConversation(conv);
          }}
          onSendMessage={handleSendMessage}
          onDownload={handleDownload}
          showDownloadButton={showDownloadButton}
          formatTime={formatTime}
          messages={conversationMessages}
          isLoading={isSending || isAILoading}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        
        <ScenePanel
          status={status}
          onExpandToggle={() => setIsCanvasExpanded(!isCanvasExpanded)}
          isExpanded={isCanvasExpanded}
          parameters={currentParameters}
          onParameterChange={handleParameterChange}
          onParameterApply={handleParameterApply}
          showHotspots={showHotspots && !isCompiling}
        />
        
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
        }}
      />
    </div>
  );
};

// Declare global functions for WebGL and OpenSCAD
declare global {
  interface Window {
    clearScene?: () => void;
    loadWebGLData?: () => void;
    loadSTLBlob?: (blob: Blob) => void;
    startAutoRefresh?: () => void;
    stopAutoRefresh?: () => void;
    webglData?: any;
  }
}
